/* ───────────────────────────────────────────────────────────────────────────
   O16 "Pairs well with" cross-sell — always-visible add-to-cart
   ─────────────────────────────────────────────────────────────────────────────
   Cards are injected ASYNC by the Section Rendering API into
   <product-recommendations>.  This module uses event delegation so it handles
   any cards present now or added later.

   Logic per card:
   • Single-variant (has_only_default_variant or exactly 1 variant):
       POST /cart/add.js → refresh cart sections → open cart drawer.
   • Multi-variant:
       Navigate to the product page so the customer can pick a variant.
       We never silently fail.

   Styling lives in o16-product-gallery.css (loaded on every product page).
─────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  // ── Helpers ────────────────────────────────────────────────────────────────

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    // The theme's CartDrawer custom element exposes open() directly.
    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      drawer.setAttribute('open', '');
    }
  }

  // Surface a visible error in the drawer's live region instead of
  // swallowing the failure.
  function showError(message) {
    const el = document.getElementById('CartDrawer-CartErrors');
    if (!el) return;
    el.textContent =
      message ||
      (window.cartStrings && window.cartStrings.error) ||
      'Something went wrong. Please try again.';
  }

  function addToCart(variantId, btn) {
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('o16-cs-btn--loading');
    const originalLabel = btn.innerHTML;
    btn.innerHTML = '<span class="o16-spinner" aria-hidden="true"></span>';

    const cartDrawer = document.querySelector('cart-drawer');
    const sectionIds =
      cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map(function (section) { return section.id; })
        : ['cart-drawer', 'cart-icon-bubble'];

    // ONE round trip: ask /cart/add.js to render the sections we need in the
    // same response (base-theme pattern — see product-form.js) instead of a
    // slow second fetch just to re-fetch the drawer markup.
    fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sectionIds,
        sections_url: window.location.pathname
      })
    })
      .then(function (res) {
        return res.json().then(function (data) { return { ok: res.ok, data: data }; });
      })
      .then(function (result) {
        if (!result.ok || result.data.status) {
          // Shopify (or the network) returned an error — surface it instead
          // of silently doing nothing.
          showError(result.data && (result.data.description || result.data.message));
          return;
        }
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          // renderContents() also opens the drawer once contents are in.
          cartDrawer.renderContents(result.data);
        } else {
          openCartDrawer();
        }
        // Broadcast for any other cart listeners in the theme.
        //
        // IMPORTANT — source MUST be 'cart-items' (see o16-cart-drawer.js for
        // the full explanation): cart.js's CartItems.connectedCallback()
        // subscribes every <cart-drawer-items> instance to this event and,
        // unless source === 'cart-items', runs its inherited onCartUpdate()
        // which fetches /cart?section_id=main-cart-items and overwrites this
        // element with the CART PAGE's <cart-items> markup — stomping the
        // drawer we just rendered via cartDrawer.renderContents() above.
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
        }
      })
      .catch(function () {
        showError();
      })
      .finally(function () {
        btn.innerHTML = originalLabel;
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('o16-cs-btn--loading');
      });
  }

  // ── Event delegation ───────────────────────────────────────────────────────
  // Attach once to document so it catches cards that arrive via the async fetch.

  document.addEventListener('click', function (event) {
    const btn = event.target.closest('[data-o16-cs-add]');
    if (!btn) return;
    event.preventDefault();

    const variantId = btn.dataset.variantId;
    const productUrl = btn.dataset.productUrl;
    const isMultiVariant = btn.dataset.multiVariant === 'true';

    if (isMultiVariant) {
      // Navigate to the product page for variant selection.
      window.location = productUrl || btn.closest('a[href]')?.href || '#';
      return;
    }

    if (!variantId) return;

    // O16 extended-delivery consent gate — the shared guard every
    // add-to-cart path in the theme calls (see
    // assets/o16-extended-delivery-consent.js for the full contract).
    // data-o16-extended-date / data-o16-extended-type are baked directly
    // onto THIS button server-side (snippets/main-product-section.liquid),
    // only when the card's single variant resolves to a pre-order/
    // back-order slot. Resolves synchronously true for every ordinary
    // card, so this never delays a normal cross-sell add.
    if (window.O16ExtendedDelivery && typeof window.O16ExtendedDelivery.guard === 'function') {
      window.O16ExtendedDelivery.guard(btn).then(function (proceed) {
        if (proceed) addToCart(variantId, btn);
      });
      return;
    }

    addToCart(variantId, btn);
  });

  // ── Move the add-to-cart button INTO each card's image (bottom overlay) ──────
  // The recommendation snippet renders .o16-cs-atc as a sibling after the card;
  // relocate it inside the card's .card__inner (the image area) so it overlays
  // the bottom of the product image. CSS pins it there. Cards arrive async via
  // the Section Rendering API, so we run on load AND observe for injected cards.
  function placeButtons(scope) {
    (scope || document)
      .querySelectorAll('.product-recommendations__product .o16-cs-atc')
      .forEach(function (atc) {
        var slide = atc.closest('.product-recommendations__product');
        if (!slide) return;
        var media = slide.querySelector('.card__inner');
        if (media && atc.parentElement !== media) {
          media.appendChild(atc);
        }
      });
  }

  // ── Slider indicator (dots) ──────────────────────────────────────────────
  // The cross-sell is a native scroll-snap slider; without an affordance a
  // customer may not realise it scrolls (and may miss the first product). Add a
  // row of dots — one per card — that highlight the current position and jump to
  // any card on click. Built after the async cards arrive (same observer as the
  // button placement). We also reset scrollLeft to 0 so the first card is never
  // left hidden behind a stray scroll offset.
  function buildDots(scope) {
    (scope || document)
      .querySelectorAll('.complementary-products .js-recommendation-swiper')
      .forEach(function (slider) {
        var wrapper = slider.querySelector(':scope > .swiper-wrapper');
        if (!wrapper) return;
        var slides = wrapper.querySelectorAll(':scope > .swiper-slide');
        if (slides.length < 2) return; // nothing to browse

        var dots = slider.querySelector(':scope > .o16-cs-dots');
        if (dots && dots.childElementCount === slides.length) return; // already current
        if (!dots) {
          dots = document.createElement('div');
          dots.className = 'o16-cs-dots';
          slider.appendChild(dots);
        }
        dots.innerHTML = '';

        var dotEls = [];
        slides.forEach(function (slide, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'o16-cs-dot';
          b.setAttribute('aria-label', 'Go to item ' + (i + 1));
          b.addEventListener('click', function () {
            var left =
              slide.getBoundingClientRect().left -
              wrapper.getBoundingClientRect().left +
              wrapper.scrollLeft;
            // Clamp to the container's true max scroll. The align-start offset
            // of the LAST card can exceed scrollWidth - clientWidth (its "peek"
            // never fills a full container width); without clamping, a mandatory
            // scroll-snap container can settle one card short of the true end.
            // Clamping guarantees the last dot always lands exactly on the real
            // max scroll position — the fully-scrolled-to-the-end state.
            var maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
            wrapper.scrollTo({ left: Math.min(left, maxScroll), behavior: 'smooth' });
          });
          dots.appendChild(b);
          dotEls.push(b);
        });

        // Never leave the first card hidden behind a stray scroll offset.
        wrapper.scrollLeft = 0;

        function update() {
          var sl = wrapper.scrollLeft;
          var maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
          var best = 0;
          // Once the container is at (or within a rounding pixel of) its true
          // max scroll, the last card IS the current one — full stop. Relying
          // purely on nearest-start-offset math here picks the SECOND-to-last
          // card at true max scroll (the last card's own "start" offset is
          // beyond maxScroll, since its peek never fills a full container
          // width), which left the last dot never lighting up even when the
          // slider was genuinely scrolled all the way to the end.
          if (maxScroll <= 0 || sl >= maxScroll - 1) {
            best = slides.length - 1;
          } else {
            var wl = wrapper.getBoundingClientRect().left;
            var bestDist = Infinity;
            slides.forEach(function (slide, i) {
              var off = slide.getBoundingClientRect().left - wl + sl;
              var d = Math.abs(off - sl);
              if (d < bestDist - 1) {
                bestDist = d;
                best = i;
              }
            });
          }
          dotEls.forEach(function (d, i) {
            d.classList.toggle('o16-cs-dot--active', i === best);
          });
        }
        wrapper.addEventListener('scroll', update, { passive: true });
        update();
      });
  }

  // ── Slide cue — FEATURE 2 ("Pairs well with") ────────────────────────────
  // Same bouncing-chevron cue as sections/categories-list.liquid
  // (.o16-cat-slider__cue / assets/o16-slider-cue.js), reused here because
  // this row is a native scroll-snap track (Swiper is explicitly SKIPPED for
  // .complementary-products — see initSlider() above / global.js — so there
  // is no Swiper API/next-button to drive). Scrolls .swiper-wrapper directly,
  // mirroring o16-slider-cue.js's own scrollBy/loop-to-start math exactly.
  // Markup for the cue button itself is rendered server-side (both branches
  // of the 'complementary' block in main-product-section.liquid) — this
  // function only WIRES the click handler + positions it, same division of
  // labour as o16-slider-cue.js.
  function wireCue(container) {
    var wrapper = container.querySelector(':scope > .swiper-wrapper');
    var cue = container.querySelector(':scope > .o16-pairs-cue');
    if (!wrapper || !cue) return;

    var slideCount = wrapper.querySelectorAll(':scope > .swiper-slide').length;
    if (slideCount < 2) {
      // Nothing to browse — hide rather than show a dead-end control.
      cue.style.display = 'none';
      return;
    }

    // Vertical centering: `top:50%` on the OUTER container (.js-recommendation-swiper)
    // would center over the heading block too, not just the image row. Set
    // `top` explicitly to the vertical midpoint of .swiper-wrapper instead —
    // CSS keeps the translateY(-50%) that finishes the centering + the bounce.
    function reposition() {
      var containerRect = container.getBoundingClientRect();
      var wrapperRect = wrapper.getBoundingClientRect();
      cue.style.top = containerRect.height
        ? wrapperRect.top - containerRect.top + wrapperRect.height / 2 + 'px'
        : '';
    }
    reposition();
    window.addEventListener('resize', reposition, { passive: true });

    if (cue.dataset.cueWired) return;
    cue.dataset.cueWired = '1';
    cue.addEventListener('click', function () {
      var max = wrapper.scrollWidth - wrapper.clientWidth;
      var step = Math.max(wrapper.clientWidth * 0.8, 200);
      if (wrapper.scrollLeft >= max - 5) {
        wrapper.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        wrapper.scrollBy({ left: step, behavior: 'smooth' });
      }
    });
  }

  function initCues(scope) {
    (scope || document)
      .querySelectorAll('.complementary-products .js-recommendation-swiper')
      .forEach(wireCue);
  }

  function init() {
    placeButtons();
    buildDots();
    initCues();
    document.querySelectorAll('product-recommendations').forEach(function (pr) {
      new MutationObserver(function () {
        placeButtons(pr);
        buildDots(pr);
        initCues(pr);
      }).observe(pr, {
        childList: true,
        subtree: true
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
